import { Concierge, isContentConcierge } from '@/types/concierge'
import { useAuth } from '@hooks/useAuth'
import { useConciergeList } from '@hooks/useConciergeList'
import { useDrawer } from '@hooks/useDrawer'
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'

export default function ConciergeSelector() {
  const { selectedConcierge, conciergesList, setSelectedConcierge } =
    useConciergeList()
  const { isAdmin } = useAuth()
  const { isDrawerExpanded } = useDrawer()

  if (conciergesList.length <= 1) {
    return null
  }

  function handleConciergeChange(event: SelectChangeEvent<string>) {
    const conciergeId = event.target.value as string
    const newConcierge = conciergesList.find(
      (c: Concierge) => c.id === conciergeId,
    )

    console.log(
      'ConciergeSelector: Changing concierge from',
      selectedConcierge?.id,
      'to',
      conciergeId,
    )

    if (newConcierge) {
      setSelectedConcierge(newConcierge)
    } else {
      console.warn(
        'ConciergeSelector: Selected concierge not found, defaulting to first',
      )
      setSelectedConcierge(conciergesList[0])
    }
  }

  function cleanUrl(url: string): string {
    return url.replace(/^https?:\/\//, '')
  }

  function getDisplayText(concierge: Concierge, abbreviated = false): string {
    if (isContentConcierge(concierge)) {
      const display = concierge.name
      if (abbreviated) {
        return display.slice(0, 2).toUpperCase()
      }
      return isAdmin ? `${display} - ${concierge.email}` : display
    }

    const cleanedUrl = cleanUrl(concierge.url)
    if (abbreviated) {
      const parts = cleanedUrl.split('.')
      return parts.length > 1
        ? (parts[0][0] + (parts[1][0] || '')).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase()
    }
    return isAdmin ? `${cleanedUrl} - ${concierge.email}` : cleanedUrl
  }

  const sortedConcierges = sortConcierges(conciergesList)
  function sortConcierges(concs: Concierge[]): Concierge[] {
    return concs.sort((a, b) => {
      const typeCompare = a.type.localeCompare(b.type)
      if (typeCompare) return typeCompare
      return a.slug.localeCompare(b.slug)
    })
  }

  return (
    <Box data-testid='sidebar-concierge-selector'>
      <FormControl fullWidth size='small'>
        <Select
          data-testid='concierge-selector'
          value={selectedConcierge?.id || ''}
          onChange={handleConciergeChange}
          size='small'
          displayEmpty
          renderValue={(value) => {
            const concierge = sortedConcierges.find((c) => c.id === value)
            if (!concierge) return ''
            return !isDrawerExpanded
              ? getDisplayText(concierge, true)
              : getDisplayText(concierge)
          }}
          sx={{
            color: 'grey.800',
            minWidth: 0,
            '& .MuiSelect-select': {
              py: 1,
              px: isDrawerExpanded ? 2 : 1,
              textAlign: isDrawerExpanded ? 'left' : 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.2)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.3)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
            '& .MuiSelect-icon': {
              color: 'grey.800',
              display: isDrawerExpanded ? 'block' : 'none',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: 'background.paper',
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem',
                },
              },
            },
          }}
        >
          {sortedConcierges.map((c: Concierge) => (
            <MenuItem key={c.id} value={c.id} sx={{ pl: 4 }}>
              <Box>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {getDisplayText(c)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}
